import * as cn from "./FinanceApp.scss"

import * as React from "react"
import {ChangeEventHandler, FormEventHandler} from "react"
import Select from 'react-select'
import {createSelector} from 'reselect'
import {Preloader} from "../React/Preloader";
import {Finance, SpendingType, spendingTypeTitle} from "../model/Finance";
import {FINANCE_YEARS} from "../Demand/common";

enum FinanceMode {
    List = 1,
    Add,
}

interface Region {
    id: number
    title: string
    district: string
}

interface University {
    id: number
    title: string
    abbreviation: string
}

interface UniversityBranch {
    id: number
    regionId: number
    title: string
    abbreviation: string
}

interface Company {
    id: number
    divisionId: number
    regionId: number
    abbreviation: string
    isOpk: boolean
    city: string
}

interface Division {
    id: number
    title: string
}

export enum Role {
    Company = "company",
    Division = "division",
}

interface Person {
    id: number
    fullName: string
    position: string
    contacts?: string[]
}
interface User {
    id: number
    login: string
    role: Role
    crmLogged: boolean
    confirmed: boolean
    crmSentMessage: boolean
    isAllowedToEditDemand: boolean
    executor: Person
    chief?: Person
    production?: Person
}

interface FinanceData {
    finances: Finance[]
    regions: Region[]
    universities: University[]
    universityBranches: UniversityBranch[]
    companies: Company[]
    divisions: Division[]
    meta: {
        company: string
        user: User
    }
}

interface FinanceState {
    mode: FinanceMode
    data?: FinanceData
    id?: number
    isLoading: boolean
}

const getRegionOptions = createSelector(
    data => data.regions,
    data => data.universities,
    data => data.universityBranches,
    (regions, universities, universityBranches) => {
        return universityBranches
            .map(ub => {
                const university = universities.find(u => u.id === ub.universityId)
                const region = regions.find(r => r.id === ub.regionId)
                return {
                    ...ub,
                    university,
                    region,
                    label: [region.title, ub.abbreviation, ub.title, university.abbreviation, university.title].join(' '),
                    value: ub.id,
                }
            })
            .sort((a, b) => a.label > b.label ? 1 : -1)
    }
)

const formatUniversityOptionLabel = data => {
    return <>
        {data.abbreviation
            ? <>
                {data.abbreviation}
                {' '}
                <small className="muted">{data.title}</small>
            </> : data.title
        }
        {' '}
        <small className="muted">({data.region.title})</small>
    </>
}

const formatSpendingTypeOptionLabel = data => {
    return <>
        {data.label}
        {' '}
        <small className="muted">{data.description}</small>
    </>
}

interface SpendingTypeValue {
    value: SpendingType
    label: string
    description: string
}
const spendingTypeOptions: SpendingTypeValue[] = [
    {value: SpendingType.Target, label: spendingTypeTitle[SpendingType.Target], description: "Учитываются расходы на организацию практик, выплату стипендий и на иные меры социальной поддержки студентов, обучающихся по договорам о целевом обучении."},
    {value: SpendingType.Material, label: spendingTypeTitle[SpendingType.Material], description: "Учитываются расходы на разработку и модернизацию образовательных программ, приобретение учебно-лабораторного, учебно-производственного и научного оборудования. В случаях передачи в образовательную организацию материальных ценностей на безвозмездной основе в расчете показателя учитывается балансовая стоимость материальных ценностей по данным бухгалтерского учета организации на момент их передачи в образовательную организацию."},
    {value: SpendingType.Infrastructure, label: spendingTypeTitle[SpendingType.Infrastructure], description: "Учитываются расходы на проведение ремонтных работ."},
    {value: SpendingType.School, label: spendingTypeTitle[SpendingType.School], description: "Учитываются расходы на участие в днях карьеры, ярмарках вакансий, проведение иных профориентационных мероприятий для школьников и студентов."},
    {value: SpendingType.Education, label: spendingTypeTitle[SpendingType.Education], description: "Учитываются расходы на повышение квалификации, профподготовку и прохождение иных образовательных программ на базе образовательных организаций для действующих работников предприятия."},
    {value: SpendingType.Other, label: spendingTypeTitle[SpendingType.Other], description: ""},
]

interface FinanceFormProps {
    data: FinanceData
    onClose: () => void
    finance?: Finance
    submit: (finance: Omit<Finance, "id" | "companyId">) => Promise<void>
}

interface FinanceFormState {
    finance: {
        universityBranchId: number
        spendingType: SpendingType
        yearFinal: number
        yearForecast: number
        cooperationSubject: string
    }
    isLoading: boolean
}

class FinanceForm extends React.PureComponent<FinanceFormProps, FinanceFormState> {
    state: FinanceFormState = {
        finance: {
            yearFinal: 0,
            yearForecast: 0,
            cooperationSubject: "",
            universityBranchId: 0,
            spendingType: SpendingType.Target,
        },
        isLoading: false,
    }

    constructor(props) {
        super(props)

        if (props.finance) {
            this.state = {
                isLoading: false,
                finance: {
                    ...this.state.finance,
                    ...(props.finance || {}),
                },
            }
        }
    }

    setUniversityBranchId = (universityBranch: UniversityBranch) => this.setState({finance: { ...this.state.finance, universityBranchId: universityBranch.id}})
    setSpendingType = (spendingType: SpendingTypeValue) => this.setState({finance: { ...this.state.finance, spendingType: spendingType.value}})
    setYearFinal: ChangeEventHandler<HTMLInputElement> = event => this.setState({finance: { ...this.state.finance, yearFinal: parseFloat(event.target.value)}})
    setYearForecast: ChangeEventHandler<HTMLInputElement> = event => this.setState({finance: { ...this.state.finance, yearForecast: parseFloat(event.target.value)}})
    setCooperatingSubject: ChangeEventHandler<HTMLInputElement> = event => this.setState({finance: { ...this.state.finance, cooperationSubject: event.target.value}})

    submit: FormEventHandler<HTMLFormElement> = event => {
        event.preventDefault();

        console.log(this.state)

        const {
            universityBranchId,
            spendingType,
            yearFinal,
            yearForecast,
            cooperationSubject,
        } = this.state.finance

        this.setState({isLoading: true})

        this.props.submit({
            universityBranchId,
            spendingType,
            yearFinal,
            yearForecast,
            cooperationSubject,
        })
            .then(data => {
                this.setState({ isLoading: false })
                console.log(data)
                //      window.location.reload()
            })
            .catch(e => {
                console.log(e)
                this.setState({ isLoading: false })
            })
    }

    render() {
        if (this.state.isLoading) {
            return <Preloader/>
        }

        const universityBranchOptions = getRegionOptions(this.props.data)
        const universityBranch = this.state.finance.universityBranchId && universityBranchOptions.find(ub => ub.id === this.state.finance.universityBranchId)
        const spendingType = this.state.finance.spendingType && spendingTypeOptions.find(st => st.value === this.state.finance.spendingType)

        return <form onSubmit={this.submit}>
            <div className="modal fade in" tabIndex={-1} role="dialog"
                 style={{display: 'block'}}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button type="button" className="close" onClick={this.props.onClose}>
                                <span aria-hidden="true">×</span><span className="sr-only">Закрыть</span>
                            </button>
                            <h4 className="modal-title">Добавить информацию о финансировании</h4>
                        </div>
                        <div className="modal-body">
                            <Select placeholder="Образовательная организация" value={universityBranch}
                                    formatOptionLabel={formatUniversityOptionLabel} onChange={this.setUniversityBranchId}
                                    options={universityBranchOptions}/>
                            <Select placeholder="Направление расходования средств" value={spendingType}
                                    formatOptionLabel={formatSpendingTypeOptionLabel} options={spendingTypeOptions} onChange={this.setSpendingType}/>
                            <div className={cn.formInputField}>
                                <label htmlFor="yearFinal" className={cn.formInputLabel}>
                                    {FINANCE_YEARS[0]}г., факт, руб.
                                </label>
                                <input name="yearFinal" type="number" min="0" step="1" lang="ru-RU" className={cn.formInputInput}
                                       placeholder={FINANCE_YEARS[0] + "г., факт, руб."} value={this.state.finance.yearFinal || 0} onChange={this.setYearFinal}/>
                            </div>
                            <div className={cn.formInputField}>
                                <label htmlFor="yearForecast" className={cn.formInputLabel}>
                                    {FINANCE_YEARS[1]}г., план, руб.
                                </label>
                                <input name="yearForecast" type="number" min="0" step="1" lang="ru-RU" className={cn.formInputInput}
                                       placeholder={FINANCE_YEARS[1] + "г., план, руб."} value={this.state.finance.yearForecast || 0} onChange={this.setYearForecast}/>
                            </div>
                            <input type="text" className={cn.formInputInput}
                                   placeholder="Предмет сотрудничества с образовательной организацией" value={this.state.finance.cooperationSubject} onChange={this.setCooperatingSubject}/>
                        </div>
                        <div className="modal-footer">
                            <div className="btn-group">
                                <button type="submit" name="submit" className="btn btn-primary" value=""><i
                                    className="fa fa-check fa-inverse"></i> Сохранить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade in"/>
        </form>
    }
}

interface FinanceListProps {
    finances: Finance[]
    companies: Company[]
    divisions: Division[]
    universityBranches: UniversityBranch[]
    regions: Region[]
    canEdit: boolean
    edit: (id: number) => void
    delete: (id: number) => void
}

class FinanceList extends React.PureComponent<FinanceListProps> {
    render() {
        return (
            <table className={cn.list}>
                <thead>
                    <tr>
                        <th className={cn.listHeaderCell}>#</th>
                        <th className={cn.listHeaderCell}>Дивизион</th>
                        <th className={cn.listHeaderCell}>Предприятие</th>
                        <th className={cn.listHeaderCell}>Образовательная организация</th>
                        <th className={cn.listHeaderCell}>Направление расходования средств</th>
                        <th className={cn.listHeaderCell}>Предмет сотрудничества с образовательной организацией</th>
                        <th className={cn.listHeaderCell}>{FINANCE_YEARS[0]}г., факт, руб.</th>
                        <th className={cn.listHeaderCell}>{FINANCE_YEARS[1]}г., план, руб.</th>
                        <th className={cn.listHeaderCell}/>
                    </tr>
                </thead>
                <tbody>
                {this.props.finances.map(finance => {
                    const company = this.props.companies.find(company => company.id === finance.companyId)
                    const division = company && this.props.divisions.find(division => division.id === company.divisionId)
                    const universityBranch = this.props.universityBranches.find(ub => ub.id === finance.universityBranchId)
                    const spendingType = spendingTypeOptions.find(spendingType => finance.spendingType === spendingType.value)
                    const region = universityBranch && this.props.regions.find(region => region.id === universityBranch.regionId)

                    return (
                        <tr key={finance.id} className={cn.listRow}>
                            <td className={cn.listCell}>
                                {this.props.canEdit
                                    ? <div className={cn.listLink} onClick={() => this.props.edit(finance.id)}>{finance.id}</div>
                                    : finance.id}
                            </td>
                            <td className={cn.listCell}>{division && division.title}</td>
                            <td className={cn.listCell}>{company && company.abbreviation}</td>
                            <td className={cn.listCell}>
                                {region && <b>{region.title}</b>}
                                {' '}
                                {universityBranch && universityBranch.abbreviation}
                            </td>
                            <td className={cn.listCell}>{spendingType && spendingType.label}</td>
                            <td className={cn.listCell}>{finance.cooperationSubject}</td>
                            <td className={cn.listCell}>{finance.yearFinal}</td>
                            <td className={cn.listCell}>{finance.yearForecast}</td>
                            <td className={cn.listCell}>
                                {this.props.canEdit && <i className={cn.listLink + " fa fa-trash-o"} title="Удалить" onClick={() => this.props.delete(finance.id)}/>}
                            </td>
                        </tr>
                    )
                })}
                </tbody>
            </table>
        )
    }
}

export class FinanceApp extends React.PureComponent<{}, FinanceState> {
    state: FinanceState = {
        mode: FinanceMode.List,
        isLoading: true,
    }

    componentDidMount() {
       this.loadData()
    }

    loadData = () => {
        this.setState({isLoading: true})

        fetch("/finance/data")
            .then(response => response.json())
            .then<FinanceData>(data => data as any)
            .then(data=>{
                this.setState({data, isLoading: false})
            });
    }
    openAddForm = () => this.setState({
        mode: FinanceMode.Add,
        id: undefined,
    })
    openEditForm = (id: number) => {
        this.setState({
            mode: FinanceMode.Add,
            id,
        })
    }
    closeForm = () => this.setState({
        mode: FinanceMode.List,
        id: undefined,
    })
    delete = (id: number) => {
        this.setState({isLoading: true})

        return fetch("/finance/delete/" + id, {
            method: "post",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        })
            .then(res => {
                console.log(res)
                if (res.ok) {
                    return res.json();
                }

                throw res.status
            })
            .then(data => {
                if (data.errors) {
                    this.setState({isLoading: false})

                    return data
                }
                this.closeForm()
                this.loadData()
            })
    }
    submit = (finance: Omit<Finance, "id" | "companyId">) => {
        return fetch("/finance/edit" + (this.state.id ? "/" + this.state.id : ""), {
            method: "post",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(finance)
        })
            .then(res => {
                console.log(res)
                if (res.ok) {
                    return res.json();
                }

                throw res.status
            })
            .then(data => {
                if (data.errors) {
                    return data
                }
                this.closeForm()
                this.loadData()
            })
    }

    render() {
        if (!this.state.data || this.state.isLoading) {
            return <Preloader/>
        }

        const canEdit = this.state.data && this.state.data.meta.user.isAllowedToEditDemand

        return <>
            {canEdit && (
                <div className="btn-group">
                    <button className="btn btn-primary" disabled={this.state.mode === FinanceMode.Add} onClick={this.openAddForm}><i
                        className="fa fa-plus fa-inverse"></i> Добавить информацию о финансировании
                    </button>
                </div>
            )}
            {this.state.data && (
                <FinanceList
                    finances={this.state.data.finances}
                    companies={this.state.data.companies}
                    divisions={this.state.data.divisions}
                    universityBranches={this.state.data.universityBranches}
                    regions={this.state.data.regions}
                    edit={this.openEditForm}
                    delete={this.delete}
                    canEdit={canEdit}
                />
            )}
            {this.state.mode === FinanceMode.Add && (
                <FinanceForm
                    onClose={this.closeForm}
                    submit={this.submit}
                    data={this.state.data}
                    finance={this.state.id ? this.state.data.finances.find((finance: Finance) => finance.id === this.state.id) : undefined}
                />
            )}
        </>
    }
}